<template>
  <div>
    <b-overlay :show="isSending" no-wrap>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="pen" font-scale="3" animation="cylon"></b-icon>
        </div>
      </template>
    </b-overlay>
    <div class="title mb-3">{{ $t('scoreViewer.title') }}</div>
    <b-container class="filter-section"
                 v-show="availablePhases.length > 1 || availableSessions.length > 1 || availableRanksets.length > 1">
      <b-row class="text-center">
        <b-col class="filter-label">{{ $t('filters.phase') }}</b-col>
        <b-col class="filter-label">{{ $t('filters.session') }}</b-col>
        <b-col class="filter-label">{{ $t('filters.group') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col>
          <combo-or-label v-model="selectedPhase" :options="availablePhases"></combo-or-label>
        </b-col>
        <b-col>
          <combo-or-label v-model="selectedSession" :options="availableSessions"></combo-or-label>
        </b-col>
        <b-col>
          <combo-or-label v-model="selectedRankset" :options="availableRanksets"></combo-or-label>
        </b-col>
      </b-row>
    </b-container>
    <div>
      <select-with-buttons :options="availableBoards" @click="changeBoard"
                           :selected="currentBoard != null ? currentBoard.Number : 0"></select-with-buttons>
    </div>
    <template v-if="currentBoard != null">
      <div v-if="currentDeal.Hand != null" class="position-relative mt-5">
        <deal :deal="currentDeal" :overlay="currentDetailsOverlay"
              :dealNumber="selectedBoard" ref="deal"></deal>
        <div v-if="isTd && !isSimultaneous" style="font-size: 1.5rem; position: absolute; top: 0; right: 2%;">
          <b-icon-trash variant="danger" style="cursor: pointer;" @click="deleteDeal"></b-icon-trash>
        </div>
      </div>
        <div v-for="alter in availableAlternatives" :key="alter" class="mt-5">
          <div v-if="availableAlternatives.length > 1">{{ $t('scoreViewer.variant', {alter: alter}) }}</div>
          <b-table :fields="fieldsToBeShown" :items="scoreToBeShown(alter)" primary-key="key" tbody-class="resultRow">
            <template #cell(points)="row">
              <template v-if="row.item.Alter === -1">
                <b-spinner small variant="success"></b-spinner>
              </template>
              <template v-else>
                  <span v-if="row.item.PercNs != null">
                    <span v-text="bankersRound(row.item.PercNs)"
                        :class="isOurNsSide(row.item) ? 'font-weight-bold' : null"></span>/<span
                    v-text="bankersRound(row.item.PercEw)"
                    :class="isOurEwSide(row.item) ? 'font-weight-bold' : null"></span>
                </span>
                <span v-else :class="computeScoreColor(row.item.PuntiNs)">{{ row.item.PuntiNs }}</span>
              </template>
            </template>
            <template #cell(TextualContract)="row">
              <div v-html="row.value"></div>
            </template>
            <template #cell(TextualLead)="row">
              <div v-html="row.value"></div>
            </template>

            <template #cell(show_details)="row">
              <b-icon-search v-if="!row.detailsShowing" @click="openDetails(row.item.key)"></b-icon-search>
            </template>

            <template #row-details="row">
              <div class="position-relative">
              <result-details :details="currentRowDetails(row.item)" @askedOverlay="overlayDetails"
                              :askForOverlay="currentDeal.Hand != null && currentDeal.Hand.N != null"></result-details>
                <div style="font-size: 1.5rem;">

              <b-icon-trash v-if="isTd && canDeleteScore(row.item)"
                            @click="deleteScore(row.item)"
                            variant="danger" style="position: absolute; right: 2%; top: 0%;"
              ></b-icon-trash>
              </div>
              </div>
            </template>
          </b-table>
        </div>
    </template>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex';
import SelectWithButtons from '@/components/lib/SelectWithButtons';
import Deal from '@/components/display/Deal';
import ComboOrLabel from "@/components/lib/ComboOrLabel";
import ResultDetails from "../components/display/ResultDetails";
import {addColorToSuite as _addColorToSuite} from "../lib/cardSuits";
import {fetchCustomMethod} from "@/lib/fetcher";
import individualContest from "../mixins/individualContest";

export default {
  name: 'ScoreViewer',
  components: {ResultDetails, ComboOrLabel, Deal, SelectWithButtons},
  mixins: [individualContest],
  data() {
    return {
      selectedBoard: 0,
      currentDetailsId: "",
      currentDetailsOverlay: null,
      selectedPhase: 1,
      selectedSession: 1,
      selectedRankset: 1,
      isSending: false
    };
  },
  computed: {
    ...mapGetters({
      playerCurrentSessionMap: 'players/playerCurrentSessionMap',
      sectors: 'tournament/sector',
      globalDeals: 'deals/played',
      isTd: 'webApp/isTd',
      simultaneousType: 'tournament/simultaneousType',
    }),
    isSimultaneous() {
      return this.simultaneousType > 0;
    },
    availablePhases() {
      return Object.keys(this.globalDeals.Phases).sort((a, b) => a - b);
    },
    availableSessions() {
      return Object.keys(
          this.globalDeals.Phases[this.selectedPhase]?.Sessions ?? {}
      ).sort((a, b) => a - b);
    },
    availableRanksets() {
      return Object.keys(
          this.globalDeals
              .Phases[this.selectedPhase]
              ?.Sessions[this.selectedSession]
              ?.Ranksets ?? {}
      ).map(rkset => {
        return {
          value: rkset,
          text: this.globalDeals
              .Phases[this.selectedPhase]
              .Sessions[this.selectedSession]
              .Ranksets[rkset]
              .GroupedSectors.join('-')
        }
      }).sort((a, b) => a.value - b.value);
    },
    availableBoards() {
      return Object.keys(
          this.globalDeals
              .Phases[this.selectedPhase]
              ?.Sessions[this.selectedSession]
              ?.Ranksets[this.selectedRankset]
              ?.Boards ?? {}
      )
          .sort((a, b) => a - b)
          .map(key => {
            return {value: key, text: key, class: ['scoreButton']};
          });
    },
    availableAlternatives() {
      let temp = this.currentBoard.Scores?.filter(res => res.Score !== '').map(res => Math.max(0, res.Alter));
      return [...new Set(temp)].sort((a, b) => a - b);
    },
    currentBoard() {
      return this.globalDeals
          .Phases[this.selectedPhase]
          ?.Sessions[this.selectedSession]
          ?.Ranksets[this.selectedRankset]
          ?.Boards[this.selectedBoard] ?? null
    },
    currentDeal() {
      return this.globalDeals
          .Phases[this.selectedPhase]
          ?.Sessions[this.selectedSession]
          ?.Ranksets[this.selectedRankset]
          ?.Deals[this.selectedBoard] ?? {}
    },
    fieldsToBeShown() {
      return [
        {
          key: 'TextualContract',
          label: this.$t('scoreViewer.contract'),
          formatter: this.addColorToSuite
        },
        {
          key: 'TextualLead',
          label: '',
          formatter: this.addColorToSuite
        },
        {
          key: 'TextualScore',
          label: ''
        },
        {
          key: 'points',
          label: 'Pt.'
        },
        {
          key: 'show_details',
          label: ''
        }
      ];
    }
  },
  methods: {
    ...mapMutations({
      removeDealHand: 'deals/removeDealHand',
      removeScore: 'deals/removeScore',
    }),
    overlayDetails(details) {
      this.currentDetailsOverlay = details;

      var element = this.$refs["deal"].$el;
      this.$scrollTo(element, {offset: -30});
    },
    deleteDeal() {
      if (!confirm(this.$t('scoreViewer.confirmDeleteDeal'))) return;
      fetchCustomMethod('/api/deal', 'DELETE', {board: this.selectedBoard, session: this.selectedSession})
          .then(resp => {
            if (resp.success) {
              this.removeDealHand({
                phase: this.selectedPhase,
                session: this.selectedSession,
                rankset: this.selectedRankset,
                board: this.selectedBoard
              });
            } else {
              alert(this.$t('scoreViewer.errorDeleteDeal'));
            }
          });
    },
    computeScoreColor(points) {
      const intPoints = parseInt(points);
      return intPoints > 0 ? "blueResult" : intPoints < 0 ? "redResult" : "yellowResult";
    },
    changeBoard(number) {
      this.selectedBoard = number;

      this.currentDetailsOverlay = null;

      const that = this;
      that.$nextTick(() => {
            const ourResult = Object.values(this.currentBoard.Scores)
                .filter(res => res.Score !== '')
                .find(row => that.isOurScore(row));

            if (ourResult != null && this.currentBoard.Hand != null && this.currentBoard.Hand.N != null) {
              that.overlayDetails(that.currentRowDetails(ourResult));
            }
          }
      );
    },
    bankersRound(number) {
      var r = Math.round(number);
      return Math.abs(number) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r;
    },
    openDetails(number) {
      this.currentDetailsId = number;
    },
    closeDetails() {
      this.currentDetailsId = "";
    },
    scoreToBeShown(alter) {
      const that = this;
      return this.currentBoard.Scores
          .filter(res => res.Score !== '' && (res.Alter === alter || (alter === 0 && res.Alter === -1)))
          .map(row => {
            const ret = {...row};
            ret._rowVariant = that.isOurScore(row) ? "warning" : "";
            ret.key = ret.BoardNumber + "_" + ret.Round + "_" + ret.Series + "_" + ret.Group + "_" + ret.Table + "_" + ret.Alter;
            ret._showDetails = ret.key === that.currentDetailsId;
            return ret;
          }).sort((a, b) => {
            return parseInt(b.PuntiNs) - parseInt(a.PuntiNs)
          });
    },
    deleteScore(row) {
      const toBeSent = {
        board: this.selectedBoard,
        table: row.Table,
        round: row.Round,
        serie: row.Series,
        room: 0,
        group: row.Group,
        session: row.Session,
        declarer: 0,
        lead: row.Lead,
        contract: ""
      };
      this.$nextTick(() => {
        this.isSending = true;
        fetchCustomMethod('/api/score', "DELETE", toBeSent)
            .then(resp => {
              this.isSending = false;
              if (resp.success) {
                this.removeScore({
                  phase: this.selectedPhase,
                  session: this.selectedSession,
                  rankset: this.selectedRankset,
                  board: this.selectedBoard,
                  round: row.Round,
                  series: row.Series,
                  group: row.Group,
                  table: row.Table
                });
              } else {
                alert(this.$t('scoreViewer.errorDelete'));
              }
            });
      });
    },
    currentRowDetails(row) {
      const round = row.Round;
      const groupId = row.Group;
      const phase = row.Phase;
      const series = row.Series;
      const session = row.Session;
      const table = row.Table;

      const group = this.sectors.Phases[phase].Series[series].Sessions[session].Groups[groupId];

      const currentTableStatus = group.Rounds[round].Tables[table]

      return {
        N: this.playersByCode[currentTableStatus.N]?.Fullname ?? '',
        S: this.playersByCode[currentTableStatus.S]?.Fullname ?? '',
        E: this.playersByCode[currentTableStatus.E]?.Fullname ?? '',
        W: this.playersByCode[currentTableStatus.W]?.Fullname ?? '',
        ...row
      };
    },
    canDeleteScore(row) {
      const phase = row.Phase;
      const series = row.Series;
      const session = row.Session;

      const serieCurrentSession = this.sectors.Phases[phase].Series[series].CurrentSession;

      return session === serieCurrentSession;
    },
    addColorToSuite(contract) {
      return _addColorToSuite(contract);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.selectedPhase = Math.max(...this.availablePhases);
      this.$nextTick(() => {
        this.selectedSession = Math.max(...this.availableSessions);
      });
    });
  }

}

</script>

<style scoped>

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e3a5f;
  letter-spacing: -0.01em;
}

.blueResult {
  color: #1d4ed8;
  font-weight: 600;
}

.redResult {
  color: #dc2626;
  font-weight: 600;
}

.yellowResult {
  color: #7c3aed;
  font-weight: 600;
}

>>> .resultRow {
  line-height: 22px;
}

>>> .scoreButton {
  background: #fff;
  border: 1.5px solid #1e3a5f;
  color: #1e3a5f;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: background-color 0.15s, color 0.15s;
}

>>> .scoreButton:hover {
  background: #eef2f7;
}
</style>
